import * as L from "leaflet";
import { MarkerCluster } from "leaflet";
import "proj4leaflet";
import { useEffect, useMemo, useRef } from "react";
import {
  AttributionControl,
  CircleMarker,
  GeoJSON,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import trafficMessageClusterIcon from "../../assets/roadworksClusterIcon.svg";
import heightrestrictionMapIcon from "../../assets/heightRestrictionClusterIcon.svg";
import MarkerClusterGroup from "react-leaflet-cluster";
import RouteDescription from "../RouteDescription/RouteDescription";
import {
  useRouteSettings,
  useRouteSettingsActions,
} from "../../stores/RouteSettingsStore";
import {
  GetRoute,
  routeApiErrorMessage,
  SingleRouteResponse,
} from "../../services/RoutingAdapter";
import { AddressItem } from "../../services/GeocodeService";
import { useUserNotificationActions } from "../../stores/UserNotificationStore";
import endIconResource from "../../assets/toMarker.svg";
import startIconResource from "../../assets/fromMarker.svg";
import viaIconResource from "../../assets/viaMarker.svg";
import blockIconResource from "../../assets/blockMarker.svg";
import {
  useClickedFeature,
  useHighlightFeature,
  useRoadFeatureHighlightLine,
  useRouteDescriptionHighlightFeature,
} from "../../stores/RouteHighlightStore";
import {
  useRouteLocationActions,
  useBarrierPoints,
  useFromAddress,
  useToAddress,
  useViaPoints,
} from "../../stores/RouteLocationStore";
import {
  useRouteStoreActions,
  useRouteResponse,
  useSelectedRouteIndex,
  useIsLoading,
  RoadFeatureResponse,
  useRoadFeatureResponse,
  useControlsOpen,
} from "../../stores/RouteStore";
import {
  useClickedRoadFeature,
  useRoadFeatureDialogIsOpen,
  useRouteFeatureDialogStoreActions,
} from "../../stores/RouteFeatureDialogStore";
import { CircularProgress, Stack } from "@mui/material";
import { HeightRestrictions } from "./MapLayers/HeightRestrictions";
import { TrafficMessages } from "./MapLayers/TrafficMessages";
import { MapLayerSelector } from "./MapLayerSelector/MapLayerSelector";
import BomstasjonMarkers from "./markers/BomstasjonMarkers";
import DognhvileplassMarkers from "./markers/DognhvileplassMarkers";
import VegsperringMarkers from "./markers/VegsperringMarkers";
import VarutsattVegMarkers from "./markers/VarutsattVegMarkers";
import TrafikkreguleringMarkers from "./markers/TrafikkreguleringMarkers";
import HeightRestrictionMarkers from "./markers/HeightRestrictionMarkers";
import RasteplassMarkers from "./markers/RasteplassMarkers";
import VegkameraMarkers from "./markers/VegkameraMarkers";
import { GetStateFromUrl } from "../../services/UrlService";
import { confirmFirstRender, firstRenderHasRun } from "../../App";
import { useRouteFeatureMarkersState } from "../../stores/MapLayers/RouteFeatureMarkersStore";
import SearchMenu from "./SearchMenu";
import { RoadFeautureDialog } from "../common/RoadFeatureDialog";
import { Page } from "../common/Page";
import {
  useEnvironmentName,
  useEnvironmentSettingActions,
} from "../../stores/EnvironmenSettingStore";
import { TrafficMessageMarkers } from "./markers/TrafficMessageMarker";
import LadestasjonMarkers from "./markers/LadestasjonMarkers";
import SelectedRoute from "./SelectedRoute";
import AlternativeRoute from "./AlternativeRoute";
import ObstructedRoute from "./ObstructedRoute";
import MeasurementSitesMarkers from "./markers/MeasurementSiteMarkers";
import TunnelMarkers from "./markers/TunnelMarkers";

//TODO: enable when NVDB is ready
//import BruMarkers from "./markers/BruMarkers";

import { useObstructingFeatureMarkersState } from "../../stores/MapLayers/ObstructedRouteMarkerStore";
import VektMarkers from "./markers/VektMarkers";
import LengdeMarkers from "./markers/LengdeMarkers";
import JernbanekryssMedFareMarkers from "./markers/JernbanekryssMedFareMarkers";
import FergeMarkers from "./markers/FergeMarkers";
import TuristvegMarkers from "./markers/TuristvegMarkers";
import GjennomkjoringForbudtMarkers from "./markers/GjennomkjoringForbudtMarkers";

import { useRoadFeatureResponseMarkersState } from "../../stores/MapLayers/RoadFeatureResponseMarkersStore";
import { useRouteOnMapStoreState } from "../../stores/MapLayers/RouteOnMapStore";
import RoadFeatureApiView from "./RoadFeaturesApiTool/RoadFeatureApiView";
import RoadFeatureLine from "./MapLayers/RoadFeatureLine";

const position: L.LatLngExpression = [60.877003, 8.90353];
const mapResolutions = [
  21674.7100160867, 10837.3550080434, 5418.67750402168, 2709.33875201084,
  1354.66937600542, 677.334688002709, 338.667344001355, 169.333672000677,
  84.6668360003387, 42.3334180001693, 21.1667090000847, 10.5833545000423,
  5.29167725002117, 2.64583862501058, 1.32291931250529, 0.661459656252646,
];

const crs = new L.Proj.CRS(
  "EPSG:25833",
  "+proj=utm +zone=33 +ellps=GRS80 +units=m +no_defs",
  {
    resolutions: mapResolutions,
    origin: [-2500000.0, 9045984.0],
  }
);

const startIcon = L.divIcon({
  className: "",
  html: `<img src=${startIconResource} />`,
  iconSize: [50, 50],
  iconAnchor: [25, 50],
});

const endIcon = L.divIcon({
  className: "",
  html: `<img src=${endIconResource} />`,
  iconSize: [50, 50],
  iconAnchor: [25, 50],
});

const viaIcon = L.divIcon({
  className: "",
  html: `<img src=${viaIconResource} />`,
  iconSize: [50, 50],
  iconAnchor: [25, 50],
});

const blockIcon = L.divIcon({
  className: "",
  html: `<img src=${blockIconResource} />`,
  iconSize: [50, 50],
  iconAnchor: [25, 50],
});

function convertAddress(addressItem: AddressItem): L.LatLngExpression {
  const item = addressItem.location as unknown as [number, number];
  return [item[1], item[0]];
}

function createListElement(): HTMLLIElement {
  return L.DomUtil.create("li");
}

function createButton(): HTMLButtonElement {
  return L.DomUtil.create("button", "contextPopupButton");
}

function createContextMenu(clickHandler: (id: string) => void) {
  const list = L.DomUtil.create("ul", "contextMenuList");
  const fromButton = createButton();
  fromButton.innerText = "Fra";
  fromButton.onclick = () => {
    clickHandler("from");
  };
  const item1 = createListElement();
  item1.appendChild(fromButton);
  list.appendChild(item1);

  const viaButton = createButton();
  viaButton.innerText = "Via";
  viaButton.onclick = () => {
    clickHandler("via");
  };
  const item2 = createListElement();
  item2.appendChild(viaButton);
  list.appendChild(item2);

  const toButton = createButton();
  toButton.innerText = "Til";
  toButton.onclick = () => {
    clickHandler("to");
  };
  const item3 = createListElement();
  item3.appendChild(toButton);
  list.appendChild(item3);

  const blockButton = createButton();
  blockButton.innerText = "Blokker";
  blockButton.onclick = () => {
    clickHandler("block");
  };
  const item4 = createListElement();
  item4.appendChild(blockButton);
  list.appendChild(item4);

  return list;
}


function ContextMenu() {
  const { setFrom, setTo, addViaPoint, addBarrierPoint } =
    useRouteLocationActions();
  const viaPoints = useViaPoints();
  const barrierPoints = useBarrierPoints();

  const map = useMapEvents({
    contextmenu: (event) => {
      const popup = L.popup()
        .setContent(
          createContextMenu((buttonClicked) => {
            if (buttonClicked === "from") {
              setFrom({
                name: event.latlng.toString(),
                location: [event.latlng.lng, event.latlng.lat],
                order: 0,
              });
              popup.close();
            }

            if (buttonClicked === "to") {
              setTo({
                name: event.latlng.toString(),
                location: [event.latlng.lng, event.latlng.lat],
                order: 0,
              });
              popup.close();
            }

            if (buttonClicked === "via") {
              addViaPoint({
                name: event.latlng.toString(),
                location: [event.latlng.lng, event.latlng.lat],
                order: viaPoints.length + 1,
              });
              popup.close();
            }

            if (buttonClicked === "block") {
              addBarrierPoint({
                name: event.latlng.toString(),
                location: [event.latlng.lng, event.latlng.lat],
                order: barrierPoints.length + 1,
              });
              popup.close();
            }
          })
        )
        .setLatLng(event.latlng)
        .addTo(map)
        .openOn(map);
    },
  });
  return <></>; 
}

interface IDraggableMarkerProps {
  id: string;
  position: L.LatLngExpression;
  icon: L.DivIcon;
  setPosition: (position: AddressItem) => void;
}

function DraggableMarker(props: IDraggableMarkerProps) {
  const markerRef = useRef<L.Marker>(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const latlng = marker.getLatLng();
          const addressItem: AddressItem = {
            name: latlng.toString(),
            location: [latlng.lng, latlng.lat],
            order: 0,
          };
          props.setPosition(addressItem);
        }
      },
    }),
    [props]
  );

  return (
    <Marker
      key={props.id}
      draggable={true}
      eventHandlers={eventHandlers}
      position={props.position}
      ref={markerRef}
      icon={props.icon}
    ></Marker>
  );
}

function RightPanel() {
  return (
    <Stack
      spacing={2}
      style={{
        width: "300px",
        float: "right",
        margin: "15px",
        paddingTop: "20px",
        zIndex: "10000",
        maxHeight: "90vh",
        overflow: "overlay",
        overflowY: "auto",
      }}
    >
      <SearchMenu />
    </Stack>
  );
}

function ClusterIconHeightRestriction(cluster: MarkerCluster) {
  const height = cluster.getAllChildMarkers()[
    Math.round(cluster.getChildCount() / 2)
  ].options.alt as unknown as number;
  return L.divIcon({
    className: "height-restriction-cluster",
    html:
      '<img style="height: 2.75rem" alt="Samling av H�ydebegrensninger" src="' +
      heightrestrictionMapIcon +
      '"/><div class="height-restriction-cluster-icon-text">' +
      (Math.round(height * 100) / 100).toFixed(1) +
      '</div><div class="height-restriction-marker-cluster-text">' +
      cluster.getChildCount() +
      "</div>",
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  });
}

function ClusterIconTrafficMessages(cluster: MarkerCluster) {
  return L.divIcon({
    className: "traffic-message-cluster",
    html:
      '<img style="height: 2.75rem" alt="Samling av trafikkmeldinger" src="' +
      trafficMessageClusterIcon +
      '"/><div class="traffic-message-marker-cluster-text">' +
      cluster.getChildCount() +
      "</div>",
    iconSize: [40, 40],
  });
}

function HighlightedFeatures() {
  const clickedFeature = useClickedFeature();
  const map = useMap();

  useEffect(() => {
    if (clickedFeature !== null) {
      const bounds = L.latLngBounds(
        clickedFeature.geometry.coordinates.map((c) => {
          return [c[1], c[0]];
        })
      );

      map.fitBounds(bounds, { maxZoom: 15 });
    }
  }, [clickedFeature, map]);

  return <></>;
}

interface IRouteMarkerProps {
  route: SingleRouteResponse;
}

function RouteMarkers(props: IRouteMarkerProps) {
  const { route } = props;

  const displayRouteFeatureMarkers = useRouteFeatureMarkersState();

  if (displayRouteFeatureMarkers) {
    return (
      <>
       {
          route.features.map((feature) => {
            return (
              <>
                <BomstasjonMarkers bomstasjoner={feature.properties.roadFeatures.bomstasjoner} />
                <DognhvileplassMarkers dognhvileplasser={feature.properties.roadFeatures.dognhvileplasser} />
                <VegsperringMarkers vegsperringer={feature.properties.roadFeatures.vegsperringer} />
                <VarutsattVegMarkers varutsatteVeger={feature.properties.roadFeatures.varutsatteVeger} />
                <HeightRestrictionMarkers heightRestrictions={feature.properties.roadFeatures.heights} />
                <RasteplassMarkers rasteplasser={feature.properties.roadFeatures.rasteplasser} />
                <VegkameraMarkers vegkameraer={feature.properties.roadFeatures.vegkameraer} />                
                <TrafficMessageMarkers roadFeatures={feature.properties.roadFeatures} />  
                <LadestasjonMarkers ladestasjoner={feature.properties.roadFeatures.ladestasjoner}/>
                <MeasurementSitesMarkers measurementSites={feature.properties.roadFeatures.measurementSites} />
                <JernbanekryssMedFareMarkers jernbanekryss={feature.properties.roadFeatures.jernbanekryssingerMedSaerskiltFare}/>
                <TunnelMarkers tunneler={feature.properties.roadFeatures.tunneler} />
                <TrafikkreguleringMarkers trafikkreguleringer={feature.properties.roadFeatures.trafikkreguleringer} />
                <FergeMarkers ferger={feature.properties.roadFeatures.ferger} />
                <GjennomkjoringForbudtMarkers gjennomkjoringForbudtFeatures={feature.properties.roadFeatures.gjennomkjoringForbudt} />
                {/*
                //TODO: enable when NVDB is ready 
                <BruMarkers bruer={feature.properties.roadFeatures.bruer} />
                */}
              </>
            )
          })
       }
      </>
    );
  }

  return <></>;
}

function ObstructedRouteMarkers(props: IRouteMarkerProps) {
  const { route } = props;
  
  const displayObstructingFeatureMarkers = useObstructingFeatureMarkersState();

  if(route.obstructingFeatures == null) {
    return (<></>)
  }

  if(displayObstructingFeatureMarkers) {
    return (
      <>
          <BomstasjonMarkers bomstasjoner={route.obstructingFeatures.bomstasjoner} />
          <RasteplassMarkers rasteplasser={route.obstructingFeatures.rasteplasser} />
          <VegkameraMarkers vegkameraer={route.obstructingFeatures.vegkameraer} />
          <HeightRestrictionMarkers heightRestrictions={route.obstructingFeatures.heights} />
          <DognhvileplassMarkers dognhvileplasser={route.obstructingFeatures.dognhvileplasser} />
          <LadestasjonMarkers ladestasjoner={route.obstructingFeatures.ladestasjoner} />
          <LengdeMarkers lengdebegrensninger={route.obstructingFeatures.lengths} />
          <TuristvegMarkers turistveger={route.obstructingFeatures.turistVeger} />
          <VektMarkers vektbegrensninger={route.obstructingFeatures.weights} />
          <FergeMarkers ferger={route.obstructingFeatures.ferger} />
          <TrafficMessageMarkers roadFeatures={route.obstructingFeatures} />
          <VegsperringMarkers vegsperringer={route.obstructingFeatures.vegsperringer} />
          <VarutsattVegMarkers varutsatteVeger={route.obstructingFeatures.varutsatteVeger} />
          <MeasurementSitesMarkers measurementSites={route.obstructingFeatures.measurementSites} />
          <JernbanekryssMedFareMarkers jernbanekryss={route.obstructingFeatures.jernbanekryssingerMedSaerskiltFare} />
          <TunnelMarkers tunneler={route.obstructingFeatures.tunneler} />
          <TrafikkreguleringMarkers trafikkreguleringer={route.obstructingFeatures.trafikkreguleringer} />
          <GjennomkjoringForbudtMarkers gjennomkjoringForbudtFeatures={route.obstructingFeatures.gjennomkjoringForbudt} />
          {/*
          //TODO: enable when NVDB is ready
          <BruMarkers bruer={route.obstructingFeatures.bruer} />
          */}
      </>
    )
  }

  return <></>;
}

interface IRoadFeatureResponseMarkersProps {
  roadFeatureResponse: RoadFeatureResponse | undefined;
}

function RoadFeatureResponseMarkers(props: IRoadFeatureResponseMarkersProps) {
  const {roadFeatureResponse} = props;
  const displayRoadFeatureResponseMarkers = useRoadFeatureResponseMarkersState();
  
  if(roadFeatureResponse === undefined) {
    return(<></>);
  }


  if(displayRoadFeatureResponseMarkers) {
    return(
      <>
        <BomstasjonMarkers bomstasjoner={roadFeatureResponse.bomstasjoner} />
        <DognhvileplassMarkers dognhvileplasser={roadFeatureResponse.dognhvileplasser} />
        <VegsperringMarkers vegsperringer={roadFeatureResponse.vegsperringer} />
        <VarutsattVegMarkers varutsatteVeger={roadFeatureResponse.varutsatteVeger} />
        <HeightRestrictionMarkers heightRestrictions={roadFeatureResponse.heights} />
        <RasteplassMarkers rasteplasser={roadFeatureResponse.rasteplasser} />
        <VegkameraMarkers vegkameraer={roadFeatureResponse.vegkameraer} />                
        <TrafficMessageMarkers roadFeatures={roadFeatureResponse} />  
        <LadestasjonMarkers ladestasjoner={roadFeatureResponse.ladestasjoner}/>
        <MeasurementSitesMarkers measurementSites={roadFeatureResponse.measurementSites} />
        <JernbanekryssMedFareMarkers jernbanekryss={roadFeatureResponse.jernbanekryssingerMedSaerskiltFare}/>
        <TunnelMarkers tunneler={roadFeatureResponse.tunneler} />
        <FergeMarkers ferger={roadFeatureResponse.ferger} />
        <TrafikkreguleringMarkers trafikkreguleringer={roadFeatureResponse.trafikkreguleringer} />
        <GjennomkjoringForbudtMarkers gjennomkjoringForbudtFeatures={roadFeatureResponse.gjennomkjoringForbudt} /> 

        {/* TODO: in the roadfeatureresponse gjkj location is null because of some mismatch between its measure and 
        the link measures. CreateLocation in the backend can't find a match. 
         */}

        {/*
          //TODO: enable when NVDB is ready
          <BruMarkers bruer={route.obstructingFeatures.bruer} />
          */}
      </>
    )
  }

  return(<></>)
}

export function LeafletMap() {
  const fromAddress = useFromAddress();
  const toAddress = useToAddress();
  const viaPoints = useViaPoints();
  const barrierPoints = useBarrierPoints();
  const {
    setFrom,
    setTo,
    addViaPoint,
    removeViaPoint,
    addBarrierPoint,
    removeBarrierPoint,
  } = useRouteLocationActions();

  const clickedRoadFeature = useClickedRoadFeature();
  const roadFeatureDialogIsOpen = useRoadFeatureDialogIsOpen();
  const { setRoadFeatureDialogIsOpen } = useRouteFeatureDialogStoreActions();

  const feature = useHighlightFeature();
  const routeDescriptionFeatureHighlight = useRouteDescriptionHighlightFeature();
  const roadFeatureLine = useRoadFeatureHighlightLine();

  const { setFeedbackMessage } = useUserNotificationActions();

  const routeResponse = useRouteResponse();
  const routeOnMapStoreState = useRouteOnMapStoreState();
  const roadFeatureResponse = useRoadFeatureResponse();
  const selectedRouteIndex = useSelectedRouteIndex();
  const routeStoreActions = useRouteStoreActions();
  const routeSettingsActions = useRouteSettingsActions();
  const routeLocationStoreActions = useRouteLocationActions();
  const isLoading = useIsLoading();
  const environmentName = useEnvironmentName();
  const routeSettings = useRouteSettings();
  const { setEnvironment } = useEnvironmentSettingActions();
  const controlsOpen = useControlsOpen();

  useEffect(() => {
    if (firstRenderHasRun) {
      return;
    }

    const parameters = GetStateFromUrl();

    if (parameters.settings) {
      routeSettingsActions.setRouteSettings(parameters.settings);
    }

    if (parameters.from) {
      routeLocationStoreActions.setFrom(parameters.from);
    }

    if (parameters.to) {
      routeLocationStoreActions.setTo(parameters.to);
    }

    if (parameters.via) {
      routeLocationStoreActions.addViaPoints(parameters.via);
    }

    if (parameters.barriers) {
      routeLocationStoreActions.addBarrierPoints(parameters.barriers);
    }

    if (parameters.environment) {
      setEnvironment(parameters.environment);
    }

    confirmFirstRender();
  }, [routeLocationStoreActions, routeSettingsActions, setEnvironment]); 

  useEffect(() => {
    if (fromAddress !== null && toAddress !== null) {
      routeStoreActions.setIsLoading(true);
      GetRoute(
        routeSettings,
        fromAddress,
        toAddress,
        viaPoints,
        barrierPoints,
        environmentName
      )
        .then((route) => {
          routeStoreActions.setRouteResponse(route);
          routeStoreActions.setSelectedRouteIndex(0);
          routeStoreActions.setRouteDrawerOpen(true);
          routeStoreActions.setIsLoading(false);
        })
        .catch((error) => {
          setFeedbackMessage({
            message: routeApiErrorMessage(error),
            severity: "error",
          });
          routeStoreActions.setRouteResponse(null);
          routeStoreActions.setIsLoading(false);
          routeStoreActions.setRouteDrawerOpen(false);
        });
    }
  }, [fromAddress, toAddress, viaPoints, barrierPoints, environmentName, routeSettings, routeStoreActions, setFeedbackMessage]);

  return (
    
    <Page>
      <MapContainer
        zoomControl={false}
        center={position}
        attributionControl={false}
        zoom={4}
        id="map"
        crs={crs}
        style={{ width: "100vw", height: "100vh" }}
      >
        <TileLayer
          attribution="&copy; NVDB, Geovekst, kommunene og Open Street Map contributors (utenfor Norge)"
          url="https://nvdbcache.geodataonline.no/arcgis/rest/services/Trafikkportalen/GeocacheTrafikkJPG/MapServer/tile/{z}/{y}/{x}"
          subdomains="123456789"
          maxZoom={(crs as any).options.resolutions.length - 1}
          minZoom={0}
        />

        {controlsOpen && (<RoadFeatureApiView/>)}

        {routeOnMapStoreState && 
        (<>
          <ObstructedRoute />
          <AlternativeRoute />
          <SelectedRoute /> 
        </>)}
          
        

        {fromAddress && (
          <DraggableMarker
            id="from-marker"
            icon={startIcon}
            position={convertAddress(fromAddress)}
            setPosition={setFrom}
          />
        )}

        {toAddress && (
          <DraggableMarker
            id="to-marker"
            position={convertAddress(toAddress)}
            icon={endIcon}
            setPosition={setTo}
          />
        )}

        {routeResponse &&
          routeResponse.routes &&
          routeResponse.routes.length > 0 && (
            <ObstructedRouteMarkers route={routeResponse!.routes[selectedRouteIndex]} />
          )}

        {routeResponse &&
          routeResponse.routes &&
          routeResponse.routes.length > 0 && (
            <RouteMarkers route={routeResponse!.routes[selectedRouteIndex]} />
          )}

        { roadFeatureResponse && (
          <RoadFeatureResponseMarkers roadFeatureResponse={roadFeatureResponse}/>
        )}
        
        
        {viaPoints.length > 0 &&
          viaPoints.map((viaPoint) => {
            return (
              <DraggableMarker
                key={"via-marker" + viaPoint.name}
                id={"via-marker" + viaPoint.name}
                position={convertAddress(viaPoint)}
                icon={viaIcon}
                setPosition={(addressItem: AddressItem) => {
                  const currentOrder = viaPoint.order;
                  removeViaPoint(viaPoint);
                  addViaPoint({ ...addressItem, order: currentOrder });
                }}
              />
            );
          })}

        {barrierPoints.length > 0 &&
          barrierPoints.map((barrierPoint) => {
            return (
              <DraggableMarker
                key={"block-marker" + barrierPoint.name}
                id={"block-marker" + barrierPoint.name}
                position={convertAddress(barrierPoint)}
                icon={blockIcon}
                setPosition={(addressItem: AddressItem) => {
                  const currentOrder = barrierPoint.order;
                  removeBarrierPoint(barrierPoint);
                  addBarrierPoint({ ...addressItem, order: currentOrder });
                }}
              />
            );
          })}
        {feature && (
          <GeoJSON
            key={`featureGeoJSON${feature.type + Math.random()}`}
            data={feature}
            style={{ color: "#00FF00" }}
          />
        )}

        {roadFeatureLine && (
          <RoadFeatureLine data={roadFeatureLine} />
        )}

        {routeDescriptionFeatureHighlight && (
          <CircleMarker radius={15} center={[routeDescriptionFeatureHighlight.location!.y, routeDescriptionFeatureHighlight.location!.x ]} />
        )}

        <HighlightedFeatures />

        <MarkerClusterGroup
          iconCreateFunction={ClusterIconHeightRestriction}
          disableClusteringAtZoom={10}
          maxClusterRadius={100}
          polygonOptions={{ opacity: 0.0, fillOpacity: 0.0 }}
        >
          <HeightRestrictions />
        </MarkerClusterGroup>

        <MarkerClusterGroup
          iconCreateFunction={ClusterIconTrafficMessages}
          disableClusteringAtZoom={10}
          maxClusterRadius={100}
          polygonOptions={{ opacity: 0.0, fillOpacity: 0.0 }}
        >
          <TrafficMessages />
        </MarkerClusterGroup>

        <ContextMenu />
        <RightPanel />
        <RouteDescription />
        
                
        {/* <RoutingParameters /> */}
        {isLoading && (
          <CircularProgress
            style={{
              zIndex: 12000,
              position: "absolute",
              marginTop: "50vh",
              marginLeft: "50vw",
            }}
          />
        )}
        <MapLayerSelector />
        {clickedRoadFeature && (
          <RoadFeautureDialog
            feature={clickedRoadFeature}
            isOpen={roadFeatureDialogIsOpen}
            onClose={() => {
              setRoadFeatureDialogIsOpen(false);
            }}
          />
        )}
        <AttributionControl position="bottomright" prefix={false} />
      </MapContainer>
    </Page>
  );
}
